import React, { useMemo } from 'react';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  type FilterType,
} from '@syncfusion/ej2-react-grids';
import { Box, Chip } from '@mui/material';
import type { ClaimQueueItemView } from '../../../data/views';
import { fcColors } from '../../../../../styles/theme';
import { useClaimNavigation } from '../hooks/useClaimNavigation';

interface ClaimsGridProps {
  items: ClaimQueueItemView[];
  testId?: string;
}

const pageSettings = { pageSize: 100, pageSizes: [25, 50, 100, 200] };
const filterSettings = { type: 'Excel' as FilterType };

const gridContainerStyles = {
  '& .e-grid': {
    border: `1px solid ${fcColors.border}`,
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: fcColors.background,
    borderBottom: `1px solid ${fcColors.border}`,
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: fcColors.dark,
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: fcColors.background,
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
  '& .e-grid .e-rowcell': {
    whiteSpace: 'normal',
    verticalAlign: 'top',
  },
};

/** Formats a `YYYY-MM-DD`-leading backend date as a local calendar date; '-' when absent. */
const formatCodingCompleteDate = (row: ClaimQueueItemView): React.ReactElement => {
  const iso = row.codingCompleteDate;
  if (!iso)
    return <span>-</span>;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);

  return <span>{Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString()}</span>;
};

const payerNameTemplate = (row: ClaimQueueItemView): React.ReactElement => <span>{row.payerName ?? '-'}</span>;

/**
 * Per-case flags: no claim ever linked, a sync fault, and an approaching or past-due filing
 * deadline (171 US3 — `hasFilingDeadline` is optional, so an older API build renders no label).
 */
const noClaimFlagTemplate = (row: ClaimQueueItemView): React.ReactElement | null => {
  const hasAnyFlag = row.hasNoClaim || row.hasSyncErrors || row.hasFilingDeadline === true;
  if (!hasAnyFlag)
    return null;
  return (
    <span style={{ display: 'inline-flex', gap: 8 }}>
      {row.hasNoClaim && <span data-testid="case-no-claim-flag">No Claim</span>}
      {row.hasSyncErrors && (
        <span data-testid="case-sync-error-flag" style={{ color: '#b45309' }}>
          Sync Error
        </span>
      )}
      {row.hasFilingDeadline && (
        <span data-testid="case-deadline-flag" style={{ color: '#b45309' }}>
          Deadline
        </span>
      )}
    </span>
  );
};

/**
 * Claim-state summary ("Status") — conveys the distinct TruBridge claim states across ALL of the
 * case's claims (never just one), rendered verbatim from the backend-joined string. Since 099
 * every claim is TruBridge-discovered, so no source guard: absent summary simply means no linked
 * claims yet.
 */
const claimStateSummaryTemplate = (row: ClaimQueueItemView): React.ReactElement | null =>
  row.claimStateSummary ? (
    <span data-testid="case-claim-state-summary">{row.claimStateSummary}</span>
  ) : null;

/**
 * One warning chip per distinct finding identity across the case's claims — the same
 * `family/finding-id` chip the claim detail page renders, so the queue doubles as the review
 * worklist. Renders nothing when the case is clean, and nothing when the server sent no
 * `findings` member at all — a deploy skew must not white-screen the worklist.
 */
const findingsTemplate = (row: ClaimQueueItemView): React.ReactElement | null =>
  !row.findings?.length ? null : (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
      data-testid="case-findings-list"
    >
      {row.findings.map((finding) => (
        <Chip
          key={finding}
          label={finding}
          size="small"
          color="warning"
          sx={{
            maxWidth: '100%',
            height: 'auto',
            alignSelf: 'flex-start',
            '& .MuiChip-label': {
              py: 0.5,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              overflow: 'visible',
              textOverflow: 'unset',
            },
          }}
          data-testid={`case-finding-chip-${finding.replace('/', '-')}`}
        />
      ))}
    </span>
  );

/**
 * Read-only Syncfusion grid for the C1 claims queue (FR-023, FR-026). Columns: patient, account,
 * center, payer, coding-complete date, claim-state summary ("Status"), no-claim flag (FR-017).
 * The case-status column is gone — the queue only lists `CodingComplete` cases (Seeded is
 * filtered out upstream). The 073 resubmission indicator is gone too: since 099 every claim is
 * discovered via the TruBridge targeted search, so "TruBridge-sourced" stopped distinguishing
 * anything — the no-claim flag already covers the only remaining split. Row selection navigates
 * to the US2 claim detail page (`useClaimNavigation`) — no other edit affordances (US4: manual UI
 * is read-only; write actions land in later CAM slices).
 */
const ClaimsGrid: React.FC<ClaimsGridProps> = ({ items, testId = 'claims-grid' }) => {
  const { handleRowSelected } = useClaimNavigation();

  const columns = useMemo(
    () => [
      { field: 'patientName', headerText: 'Patient', width: '160' },
      { field: 'accountNumber', headerText: 'Account', width: '130' },
      { field: 'center', headerText: 'Center', width: '110' },
      { field: 'payerName', headerText: 'Payer', width: '160', template: payerNameTemplate },
      {
        field: 'codingCompleteDate',
        headerText: 'Coding Complete',
        width: '150',
        template: formatCodingCompleteDate,
      },
      {
        field: 'claimStateSummary',
        headerText: 'Status',
        width: '220',
        template: claimStateSummaryTemplate,
      },
      // Array-valued column: the Excel-filter operator submenu can't infer a type for it and
      // crashes on hover, so force plain checkbox filtering (no operator submenu) here.
      {
        field: 'findings',
        headerText: 'Findings',
        width: '220',
        template: findingsTemplate,
        allowSorting: false,
        filter: { type: 'CheckBox' as FilterType },
      },
      { field: 'hasNoClaim', headerText: 'Flag', width: '140', template: noClaimFlagTemplate },
    ],
    [],
  );

  return (
    <Box sx={gridContainerStyles}>
      <GridComponent
        dataSource={items}
        allowPaging
        allowSorting
        allowFiltering
        pageSettings={pageSettings}
        filterSettings={filterSettings}
        allowTextWrap
        rowSelected={handleRowSelected}
        data-testid={testId}
      >
        <ColumnsDirective>
          {columns.map((col) => (
            <ColumnDirective key={col.field} {...col} />
          ))}
        </ColumnsDirective>
        <Inject services={[Page, Sort, Filter]} />
      </GridComponent>
    </Box>
  );
};

export default ClaimsGrid;
